import { ButtonContent } from '../ui/ButtonContent';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import type { InterviewSession } from '../../types';
import { Modal } from '../ui/Modal';
import { AnimatedLabel } from '../ui/AnimatedLabel';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import { CopyIcon } from '../icons/CopyIcon';
import { CheckIcon } from '../icons/CheckIcon';
import {
  IconUserPlus,
  IconChevronRight,
  IconArrowLeft,
  IconCode,
  IconLayoutList,
  IconAppWindow,
  IconPointer,
} from '@tabler/icons-react';
import {
  buildEmbedSnippet,
  type EmbedStyle,
} from '../../services/linkService';
import { captureException, captureEvent } from '../../lib/telemetry/posthog/frontend';
import { buildConversationCreationProps } from '../../lib/telemetry/conversation-creation-contract';
import { buildOnboardingProps } from '../../lib/telemetry/onboarding-contract';
import { useEntitlements } from '../../lib/useEntitlements';
import { FeatureGateBadge } from '../ui/FeatureGateBadge';
import { DEFAULT_EMBED_LABEL, normalizeEmbedHeight } from '../../lib/embed/options';
import { EmbedLauncherPreview } from './EmbedLauncherPreview';
import { Tooltip } from '../Tooltip';
import { InfoIcon } from '../icons';
import { matchesAllowedOrigin } from '../../lib/embed/originMatch';
import { updateSessionSettings, getInterviewSessionCount } from '../../services/interviewService';
import { IconTrash } from '@tabler/icons-react';
import { PlusIcon } from '@/components/icons';
import { ParticipantInviteView } from './ParticipantInviteView';
import type { AddParticipantsHandler } from '../../services/automationService';

/** The currently-visible body within the modal. */
export type InviteView = 'main' | 'people' | 'embed';

/** Crossfades the copy icon into its confirmation without moving the label. */
const CopyFeedbackIcon: React.FC<{ copied: boolean }> = ({ copied }) => (
  <span className="relative block h-4 w-4 shrink-0" aria-hidden="true">
    <CopyIcon className={`absolute inset-0 h-4 w-4 transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none ${copied ? 'scale-75 opacity-0' : 'scale-100 opacity-100'}`} />
    <CheckIcon className={`absolute inset-0 h-4 w-4 transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none ${copied ? 'scale-100 opacity-100' : 'scale-75 opacity-0'}`} />
  </span>
);

/** Props for {@link InviteModal}. */
export interface InviteModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /** Called when the modal should close. */
  onClose: () => void;
  /** The interview session to build invite details from. */
  session: InterviewSession;
  /** Add participants through the campaign's existing participant endpoint. */
  onInvitePeople?: AddParticipantsHandler;
  /** Open automation setup when participant invitations are not ready. */
  onOpenAutomation?: () => void;
  /** Prevent setup navigation while automation state is still resolving. */
  automationDisabled?: boolean;
  /** View shown first when this entry point opens the modal. */
  initialView?: InviteView;
}

/**
 * Modal presenting one primary share link plus focused participant and embed
 * drill-downs. Closing the modal resets it to the configured initial view.
 */
export const InviteModal: React.FC<InviteModalProps> = ({
  open,
  onClose,
  session,
  onInvitePeople,
  onOpenAutomation,
  automationDisabled = false,
  initialView = 'main',
}) => {
  const [view, setView] = useState<InviteView>(initialView);
  // Track whether onboarding.first_share_clicked has been fired.
  // Using a module-level variable to ensure it only fires once per app lifecycle.
  const hasFiredFirstShareRef = useRef(false);

  const captureShareEvent = async (method: string, extra?: Record<string, unknown>) => {
    captureEvent('conversation_creation.share_clicked', buildConversationCreationProps(session.brand_id, { method, sessionId: session.id, ...extra }));
    
    // Strong "first-time" check: if not already fired in this session, check DB.
    if (!hasFiredFirstShareRef.current && session.organization_id) {
      try {
        const count = await getInterviewSessionCount(session.organization_id);
        if (count === 1) {
          captureEvent('onboarding.first_share_clicked', buildOnboardingProps({ method }));
        }
        // Mark as fired (or attempted) so we don't hit the DB again for this user.
        hasFiredFirstShareRef.current = true;
      } catch (err) {
        // Silently fail telemetry - don't block the user's share action.
        console.error('Failed to capture onboarding share event:', err);
      }
    }
  };
  const [linkCopied, setLinkCopied] = useState(false);
  const [embedStyle, setEmbedStyle] = useState<EmbedStyle>('inline');
  const [embedCopied, setEmbedCopied] = useState(false);
  const [embedLabel, setEmbedLabel] = useState(DEFAULT_EMBED_LABEL);
  const [embedHeight, setEmbedHeight] = useState('640');
  const [embedCopyError, setEmbedCopyError] = useState(false);
  /**
   * Per-campaign embed allowlist editor state. Each row is a string the
   * user types verbatim (`https://acme.com` or `https://*.acme.com`).
   * Empty rows are dropped on save and a fully empty list saves as null
   * to preserve "embeddable anywhere" semantics.
   */
  const [allowedDomainEntries, setAllowedDomainEntries] = useState<string[]>(
    () => (session.allowed_origins && session.allowed_origins.length > 0
      ? [...session.allowed_origins]
      : []),
  );
  const [allowedDomainErrors, setAllowedDomainErrors] = useState<Set<number>>(
    new Set(),
  );
  const [allowedDomainSaving, setAllowedDomainSaving] = useState(false);
  const [allowedDomainSavedAt, setAllowedDomainSavedAt] = useState<number | null>(
    null,
  );

  const { can } = useEntitlements();
  const embedEntitled = can('embed_widget');
  const embedSnippet = useMemo(
    () =>
      buildEmbedSnippet(session.interview_link_hash, {
        style: embedStyle,
        label: embedStyle === 'inline' ? undefined : embedLabel,
        height: normalizeEmbedHeight(embedHeight),
        title: session.target_name,
      }),
    [session.interview_link_hash, session.target_name, embedStyle, embedLabel, embedHeight],
  );

  // Reset to this entry point's first view whenever the modal closes.
  useEffect(() => {
    if (!open) setView(initialView);
  }, [initialView, open]);

  const joinUrl = `${window.location.origin}/join/${session.interview_link_hash}`;

  /** Open participant entry when ready, otherwise route to automation setup. */
  const handleInvitePeopleChoice = (): void => {
    if (onInvitePeople) {
      setView('people');
      return;
    }
    if (!onOpenAutomation) return;
    onClose();
    onOpenAutomation();
  };

  /** Copy just the join URL. */
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setLinkCopied(true);
      void captureShareEvent('link');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      captureException(
        err instanceof Error ? err : String(err),
        'InviteModal.copyLink',
        { sessionId: session.id },
      );
    }
  };

  /**
   * Validate a single allowlist entry by round-tripping it through the
   * matcher: an entry is well-formed iff it matches itself when it is
   * its only allowed entry. Catches malformed schemes, multi-wildcards,
   * empty entries, etc., without re-implementing the rules.
   */
  const isAllowedDomainEntryValid = (entry: string): boolean => {
    const trimmed = entry.trim();
    if (trimmed.length === 0) return false;
    // Wildcard entries can never match themselves verbatim, so probe
    // them against a synthetic single-label subdomain.
    if (trimmed.startsWith('https://*.') || trimmed.startsWith('http://*.')) {
      const probe = trimmed.replace('://*.', '://probe.');
      return matchesAllowedOrigin(probe, [trimmed]);
    }
    return matchesAllowedOrigin(trimmed, [trimmed]);
  };

  const handleAddAllowedDomain = (): void => {
    setAllowedDomainEntries((prev) => [...prev, '']);
  };

  const handleRemoveAllowedDomain = (index: number): void => {
    setAllowedDomainEntries((prev) => prev.filter((_, i) => i !== index));
    setAllowedDomainErrors((prev) => {
      const next = new Set<number>();
      prev.forEach((errIdx) => {
        if (errIdx < index) next.add(errIdx);
        else if (errIdx > index) next.add(errIdx - 1);
      });
      return next;
    });
  };

  const handleEditAllowedDomain = (index: number, value: string): void => {
    setAllowedDomainEntries((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setAllowedDomainErrors((prev) => {
      if (!prev.has(index)) return prev;
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  };

  /**
   * Validate + persist the per-campaign allowlist. Empty rows are
   * dropped; a fully empty list saves as null (the "embeddable anywhere"
   * sentinel). Invalid non-empty rows mark the input red and abort.
   */
  const handleSaveAllowedDomains = async (): Promise<void> => {
    const errors = new Set<number>();
    allowedDomainEntries.forEach((entry, index) => {
      const trimmed = entry.trim();
      if (trimmed.length === 0) return;
      if (!isAllowedDomainEntryValid(trimmed)) errors.add(index);
    });
    if (errors.size > 0) {
      setAllowedDomainErrors(errors);
      return;
    }
    const cleaned = allowedDomainEntries
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
    const payload = cleaned.length === 0 ? null : cleaned;
    setAllowedDomainSaving(true);
    try {
      await updateSessionSettings(session.id, { allowed_origins: payload });
      setAllowedDomainErrors(new Set());
      setAllowedDomainSavedAt(Date.now());
      void captureShareEvent('embed_domain_save');
    } catch (err) {
      captureException(
        err instanceof Error ? err : String(err),
        'InviteModal.saveAllowedDomains',
        { sessionId: session.id },
      );
    } finally {
      setAllowedDomainSaving(false);
    }
  };

  /** Copy the embed snippet to clipboard. */
  const handleCopyEmbed = async () => {
    setEmbedCopyError(false);
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setEmbedCopied(true);
      void captureShareEvent('embed_snippet', { embed_style: embedStyle });
      setTimeout(() => setEmbedCopied(false), 2000);
    } catch (err) {
      captureException(
        err instanceof Error ? err : String(err),
        'InviteModal.copyEmbed',
        { sessionId: session.id },
      );
      setEmbedCopyError(true);
    }
  };

  /** Website placement, scoped customization, and copyable installation code. */
  const renderEmbedView = () => (
    <div className="max-h-[75dvh] overflow-y-auto space-y-5 pr-1">
      <button type="button" onClick={() => setView('main')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <IconArrowLeft className="h-4 w-4" /> Back
      </button>
      <div role="radiogroup" aria-label="Embed placement" className="grid grid-cols-3 gap-2">
        {([
          { value: 'inline', label: 'Inline', icon: IconLayoutList },
          { value: 'button', label: 'Popup', icon: IconAppWindow },
          { value: 'custom', label: 'Your button', icon: IconPointer },
        ] as const).map(option => (
          <label key={option.value} className={`relative flex cursor-pointer flex-col items-center gap-2 rounded-xl px-2 py-3 text-sm font-medium transition-colors ${embedStyle === option.value ? 'bg-volt text-accent-foreground' : 'bg-background text-muted-foreground hover:bg-surface-hover'}`}>
            <input type="radio" name="embed-style" value={option.value} checked={embedStyle === option.value}
              onChange={() => { setEmbedStyle(option.value); setEmbedCopied(false); }}
              className="peer sr-only" />
            <span className="pointer-events-none absolute inset-0 rounded-xl peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2" />
            <option.icon className="h-5 w-5" aria-hidden="true" />
            {option.label}
          </label>
        ))}
      </div>
      <EmbedLauncherPreview style={embedStyle} label={embedLabel} title={session.target_name} />
      {embedStyle === 'inline' ? (
        <label className="flex items-center justify-between gap-4 text-sm text-foreground">
          Embed height
          <span className="flex items-center gap-2">
            <input aria-label="Embed height" type="number" min={320} max={1600} step={40} value={embedHeight}
              onChange={event => { setEmbedHeight(event.target.value); setEmbedCopied(false); }}
              onBlur={() => setEmbedHeight(String(normalizeEmbedHeight(embedHeight)))}
              className="w-24 rounded-lg border border-border bg-background px-3 py-2 text-foreground" />
            <span className="text-muted-foreground">px</span>
          </span>
        </label>
      ) : (
        <label className="block space-y-2 text-sm text-foreground">
          <span>Button text</span>
          <input value={embedLabel} maxLength={100} onChange={event => { setEmbedLabel(event.target.value); setEmbedCopied(false); }}
            placeholder={DEFAULT_EMBED_LABEL} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground" />
        </label>
      )}
      <p className="text-sm text-muted-foreground">{embedStyle === 'inline' ? 'Paste where you want it to appear.' : embedStyle === 'custom' ? 'Keep your button’s styling and add the data attributes below.' : 'Paste where you want the button to appear.'}</p>
      <textarea
        data-testid="embed-snippet"
        aria-label="Embed code"
        readOnly
        value={embedSnippet}
        rows={4}
        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs text-muted-foreground font-mono select-all resize-none"
      />

      <div>
        <button
          data-testid="embed-copy-button"
          onClick={handleCopyEmbed}
          className="w-full py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all bg-surface-raised border border-border text-foreground hover:bg-surface-hover"
        >
          <CopyFeedbackIcon copied={embedCopied} />
          <AnimatedLabel text={embedCopied ? 'Copied!' : 'Copy code'} reserveText="Copy code" />
        </button>

        <AnimatedPresence show={embedCopyError}>
          <p role="alert" className="pt-5 text-sm text-foreground">Could not copy. Select the code above to copy it manually.</p>
        </AnimatedPresence>
      </div>

      {/* Allowed domains: per-campaign embed allowlist. Empty list =
          embeddable anywhere; populated = enforced server-side and in
          the iframe shell. */}
      <section className="space-y-2">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-bold block">
            Allowed domains
          </label>
          <p className="text-xs text-muted-foreground mt-1">
            Empty = embeddable anywhere
          </p>
        </div>
        {allowedDomainEntries.length > 0 && (
          <ul className="space-y-2">
            {allowedDomainEntries.map((entry, index) => {
              const hasError = allowedDomainErrors.has(index);
              return (
                <li key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={entry}
                    onChange={(e) => handleEditAllowedDomain(index, e.target.value)}
                    placeholder="https://example.com or https://*.example.com"
                    className={`flex-1 px-3 py-2 bg-background border rounded-lg text-sm text-foreground placeholder:text-subtle-foreground ${
                      hasError ? 'border-danger' : 'border-border'
                    }`}
                  />
                  <button
                    type="button"
                    aria-label="Remove domain"
                    onClick={() => handleRemoveAllowedDomain(index)}
                    className="p-2 text-muted-foreground hover:text-danger transition-colors"
                  >
                    <IconTrash className="w-4 h-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleAddAllowedDomain}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <PlusIcon className="w-3.5 h-3.5" />
            Add domain
          </button>
          <button aria-busy={allowedDomainSaving}
            type="button"
            onClick={handleSaveAllowedDomains}
            disabled={allowedDomainSaving}
            className={`ml-auto px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              allowedDomainSavedAt && Date.now() - allowedDomainSavedAt < 2000
                ? 'bg-success text-success-foreground'
                : 'bg-surface-raised border border-border text-foreground hover:bg-surface-hover disabled:opacity-60'
            }`}
          >
            <ButtonContent loading={allowedDomainSaving}>Save allowed domains</ButtonContent>
          </button>
        </div>
      </section>
    </div>
  );

  /** Main view: one copy action and two focused drill-down choices. */
  const renderMainView = () => (
    <div className="space-y-5">
      <section className="space-y-2">
        <label htmlFor="campaign-share-link" className="block text-sm font-medium text-foreground">
          Share link
        </label>
        <div className="flex gap-2">
          <input
            id="campaign-share-link"
            type="text"
            readOnly
            value={joinUrl}
            className="min-w-0 flex-1 select-all rounded-lg border border-border bg-background px-3 py-2 text-sm text-muted-foreground"
          />
          <button
            type="button"
            onClick={handleCopyLink}
            className="flex cursor-pointer items-center gap-2 rounded-lg button-accent px-4 py-2 text-sm font-medium transition-colors"
          >
            <CopyFeedbackIcon copied={linkCopied} />
            <AnimatedLabel text={linkCopied ? 'Copied!' : 'Copy'} reserveText="Copied!" />
          </button>
        </div>
      </section>

      <div className="overflow-hidden rounded-xl bg-surface/60">
        {(onInvitePeople || onOpenAutomation) && (
          <Tooltip content={onInvitePeople ? '' : 'Enable automation'}>
            <button
              type="button"
              aria-label={onInvitePeople ? 'Invite people' : 'Invite people. Enable automation'}
              disabled={!onInvitePeople && automationDisabled}
              onClick={handleInvitePeopleChoice}
              className="group flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-surface-hover disabled:cursor-wait disabled:opacity-60"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-wash-accent text-volt-soft">
                <IconUserPlus className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1 text-sm font-medium text-foreground">Invite people</span>
              {onInvitePeople ? (
                <IconChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
              ) : (
                <span aria-label="Enable automation" className="shrink-0 text-muted-foreground">
                  <InfoIcon aria-hidden="true" className="h-4 w-4" />
                </span>
              )}
            </button>
          </Tooltip>
        )}
        {onInvitePeople && onOpenAutomation && (
          <button
            type="button"
            onClick={() => { onClose(); onOpenAutomation(); }}
            disabled={automationDisabled}
            className="group flex w-full cursor-pointer items-center gap-3 border-t border-border/60 px-3 py-3 text-left transition-colors hover:bg-surface-hover disabled:cursor-wait disabled:opacity-60"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-wash-neutral text-muted-foreground">
              <IconLayoutList aria-hidden="true" className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1 text-sm font-medium text-foreground">Manage invitations</span>
            <IconChevronRight aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
          </button>
        )}
        {embedEntitled ? (
          <button
            type="button"
            onClick={() => setView('embed')}
            className="group flex w-full cursor-pointer items-center gap-3 border-t border-border/60 px-3 py-3 text-left transition-colors first:border-t-0 hover:bg-surface-hover"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-wash-neutral text-muted-foreground">
              <IconCode className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1 text-sm font-medium text-foreground">Embed on your site</span>
            <IconChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
          </button>
        ) : (
          <div
            className="flex w-full items-center gap-3 border-t border-border/60 px-3 py-3 opacity-60 first:border-t-0"
            aria-disabled="true"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-wash-neutral text-muted-foreground">
              <IconCode className="h-4 w-4" />
            </span>
            <span className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium text-foreground">
              Embed on your site
              <FeatureGateBadge feature="embed_widget" />
            </span>
            <IconChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={view === 'embed' ? 'Embed on your site' : 'Invite people'} maxWidth="max-w-lg">
      {view === 'embed' ? renderEmbedView() : view === 'people' && onInvitePeople ? (
        <ParticipantInviteView
          campaignId={session.id}
          onBack={() => setView('main')}
          onInvite={onInvitePeople}
          onInvited={() => {
            void captureShareEvent('automation_participants');
            onClose();
          }}
        />
      ) : renderMainView()}
    </Modal>
  );
};
